import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { extractDataFromMessage, ExtractedData } from "@/lib/chatDataExtractor";
import { playNotificationSound } from "@/lib/notificationSound";
import { createId } from "@/lib/createId";

const SESSION_STORAGE_KEY = "pixie-chat-conversation-id";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
}

export interface ConversationData {
  user_type?: "prospect" | "client";
  user_name?: string;
  user_email?: string;
  user_phone?: string;
  wedding_date?: string;
  venue?: string;
  services?: string[];
}

export function useChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationData, setConversationData] = useState<ConversationData>({});
  const [isRestoring, setIsRestoring] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const conversationDataRef = useRef<ConversationData>({});
  const conversationIdRef = useRef<string | null>(null);

  // Keep refs in sync with state
  conversationDataRef.current = conversationData;
  conversationIdRef.current = conversationId;

  // Internal function to update conversation data (uses refs to avoid stale closures)
  const updateConversationDataInternal = async (data: Partial<ConversationData>) => {
    const currentId = conversationIdRef.current;
    if (!currentId) return;

    const newData = { ...conversationDataRef.current, ...data };
    
    // Merge services arrays instead of replacing
    if (data.services && conversationDataRef.current.services) {
      newData.services = [...new Set([...conversationDataRef.current.services, ...data.services])];
    }
    
    setConversationData(newData);
    conversationDataRef.current = newData;

    try {
      await supabase
        .from("chat_conversations")
        .update({
          user_type: newData.user_type,
          user_name: newData.user_name,
          user_email: newData.user_email,
          user_phone: newData.user_phone,
          wedding_date: newData.wedding_date,
          venue: newData.venue,
          services: newData.services,
        })
        .eq("id", currentId);
    } catch (error) {
      console.error("Error updating conversation:", error);
    }
  };

  // Restore an existing conversation from sessionStorage
  const restoreConversation = useCallback(async (): Promise<string | null> => {
    const storedId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!storedId) return null;

    setIsRestoring(true);
    try {
      // Fetch conversation data
      const { data: convData, error: convError } = await supabase
        .from("chat_conversations")
        .select("*")
        .eq("id", storedId)
        .single();

      if (convError || !convData || convData.summary_sent) {
        // Conversation doesn't exist or was already ended
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        return null;
      }

      // Fetch messages
      const { data: messagesData, error: msgError } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("conversation_id", storedId)
        .order("created_at", { ascending: true });

      if (msgError) {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        return null;
      }

      // Restore state
      setConversationId(storedId);
      setConversationData({
        user_type: convData.user_type as ConversationData["user_type"],
        user_name: convData.user_name || undefined,
        user_email: convData.user_email || undefined,
        user_phone: convData.user_phone || undefined,
        wedding_date: convData.wedding_date || undefined,
        venue: convData.venue || undefined,
        services: convData.services || undefined,
      });
      setMessages(
        (messagesData || []).map((m) => ({
          id: m.id,
          role: m.role as "user" | "assistant",
          content: m.content,
          created_at: m.created_at,
        }))
      );

      return storedId;
    } catch (error) {
      console.error("Error restoring conversation:", error);
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    } finally {
      setIsRestoring(false);
    }
  }, []);

  // Create a new conversation
  const startConversation = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("chat_conversations")
        .insert({})
        .select("id")
        .single();

      if (error) throw error;
      setConversationId(data.id);
      setMessages([]);
      setConversationData({});
      
      // Save to sessionStorage
      sessionStorage.setItem(SESSION_STORAGE_KEY, data.id);
      
      // Add initial greeting - warm and open-ended
      const greeting: ChatMessage = {
        id: createId(),
        role: "assistant",
        content: "Hi! 👋 Welcome to Pixie Clicks! I'm here to help with any questions about our wedding photography, videography, or photo booth services. What can I help you with today?",
        created_at: new Date().toISOString(),
      };
      setMessages([greeting]);
      
      // Store the greeting
      await supabase.from("chat_messages").insert({
        conversation_id: data.id,
        role: "assistant",
        content: greeting.content,
      });

      return data.id;
    } catch (error) {
      console.error("Error starting conversation:", error);
      return null;
    }
  }, []);

  // Send a message and get AI response
  const sendMessage = useCallback(async (content: string) => {
    if (!conversationId || isLoading) return;

    setIsLoading(true);
    abortControllerRef.current = new AbortController();

    // Add user message optimistically
    const userMessage: ChatMessage = {
      id: createId(),
      role: "user",
      content,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      // Extract data from user message and auto-update conversation
      const extractedFromUser = extractDataFromMessage(content, "user");
      if (Object.keys(extractedFromUser).length > 0) {
        await updateConversationDataInternal(extractedFromUser);
      }

      // Store user message
      await supabase.from("chat_messages").insert({
        conversation_id: conversationId,
        role: "user",
        content,
      });

      // Call AI chat edge function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            messages: [...messages, userMessage].map((m) => ({
              role: m.role,
              content: m.content,
            })),
            conversationId,
            conversationData,
          }),
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to get AI response");
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let assistantContent = "";
      let assistantMessageId = createId();
      let textBuffer = "";

      // Add empty assistant message
      setMessages((prev) => [
        ...prev,
        {
          id: assistantMessageId,
          role: "assistant",
          content: "",
          created_at: new Date().toISOString(),
        },
      ]);

      let streamDone = false;
      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const deltaContent = parsed.choices?.[0]?.delta?.content;
            if (deltaContent) {
              assistantContent += deltaContent;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessageId
                    ? { ...m, content: assistantContent }
                    : m
                )
              );
            }
          } catch {
            // Incomplete JSON, put it back
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      // Play notification sound when response is complete
      playNotificationSound();

      // Store assistant message
      await supabase.from("chat_messages").insert({
        conversation_id: conversationId,
        role: "assistant",
        content: assistantContent,
      });
    } catch (error: any) {
      if (error.name === "AbortError") return;
      console.error("Error sending message:", error);
      
      // Add error message
      setMessages((prev) => [
        ...prev,
        {
          id: createId(),
          role: "assistant",
          content: "I'm sorry, I'm having trouble responding right now. Please try again in a moment, or feel free to reach out to us directly at office@pixiecliks.com",
          created_at: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [conversationId, messages, isLoading, conversationData]);

  // Update conversation data (public API - wraps internal function)
  const updateConversationData = useCallback(
    async (data: Partial<ConversationData>) => {
      await updateConversationDataInternal(data);
    },
    []
  );

  // End conversation and send summary
  const endConversation = useCallback(async () => {
    if (!conversationId || messages.length < 2) return;

    try {
      await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-chat-summary`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            conversationId,
          }),
        }
      );
    } catch (error) {
      console.error("Error sending summary:", error);
    }

    // Clear sessionStorage
    sessionStorage.removeItem(SESSION_STORAGE_KEY);

    // Reset state
    setConversationId(null);
    setMessages([]);
    setConversationData({});
  }, [conversationId, messages]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return {
    messages,
    isLoading,
    isRestoring,
    conversationId,
    conversationData,
    startConversation,
    restoreConversation,
    sendMessage,
    updateConversationData,
    endConversation,
  };
}
